import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack,
  IconButton,
  Typography,
} from '@mui/material';
import { useFormState } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import {
  Form, useTranslate, required, Confirm,
} from 'react-admin';
import { GrayDateInputWithUpperLabel } from './inputs/GrayDateInputWithUpperLabel';
import { getMinimumNextDate } from '../../../utils/timeGranularityUtils';

export type PlanRenewalFormValues = {
  plannedRenewalDate: string;
};

const PlanRenewalDialogTitle = (props: {
  title: string;
  onBack?: () => void;
  onClose: () => void;
}) => {

  const translate = useTranslate();
  const { isSubmitting } = useFormState();

  return (
    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {props.onBack && (
          <IconButton
            onClick={props.onBack}
            aria-label={translate('ra.action.back')}
            disabled={isSubmitting}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="body1" fontSize="20px">
          {translate(props.title)}
        </Typography>
      </Stack>
      <IconButton onClick={props.onClose} aria-label={translate('general.text.close')} disabled={isSubmitting}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  );

};

const PlanRenewalDialogActions = (props: {
  onClose: () => void;
  onSubmit: (values: PlanRenewalFormValues) => Promise<void>;
}) => {

  const translate = useTranslate();
  const { isSubmitting } = useFormState();

  return (
    <DialogActions>
      <Button onClick={props.onClose} disabled={isSubmitting}>{translate('ra.action.cancel')}</Button>
      <Button type="submit" form="plan-renewal-form" variant="contained" disabled={isSubmitting}>
        {translate('ra.action.confirm')}
      </Button>
    </DialogActions>
  );

};

export const PlanRenewalModal = (props: {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  onSubmit: (values: PlanRenewalFormValues) => Promise<void>;
  dateFieldSource?: string;
  dateFieldLabel: string;
  dateFieldHelperText?: string;
  defaultPlannedRenewalDate?: string | null;
  title: string;
  formSessionKey?: string | number;
}) => {

  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const requestClose = () => {

    setDiscardConfirmOpen(true);

  };

  const confirmDiscard = () => {

    setDiscardConfirmOpen(false);
    props.onClose();

  };

  useEffect(() => {

    if (!props.open) {

      setDiscardConfirmOpen(false);

    }

  }, [props.open]);

  const source = props.dateFieldSource ?? 'plannedRenewalDate';

  const defaultDate = props.defaultPlannedRenewalDate ? moment(props.defaultPlannedRenewalDate) : null;
  const defaultValues = {
    [source]: defaultDate?.isValid() ? defaultDate.format('YYYY-MM-DD') : '',
  };

  const handleSubmit = async (values: Record<string, unknown>) => {

    await props.onSubmit({
      plannedRenewalDate: values[source] as string,
    });

  };
  return (
    <>
      <Dialog
        open={props.open}
        keepMounted={Boolean(props.onBack)}
        maxWidth="sm"
        fullWidth
      >
        <Form
          id="plan-renewal-form"
          key={props.formSessionKey ?? 'plan-renewal'}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
        >
          <PlanRenewalDialogTitle
            title={props.title}
            onBack={props.onBack}
            onClose={requestClose}
          />
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 3 }}>
              <GrayDateInputWithUpperLabel
                source={source}
                label={props.dateFieldLabel}
                validate={required()}
                helperText={props.dateFieldHelperText}
                inputProps={{ min: getMinimumNextDate() }}
                FormHelperTextProps={{
                  sx: {
                    margin: 0,
                    marginLeft: 0,
                  },
                }}
              />
            </Stack>
          </DialogContent>
          <PlanRenewalDialogActions
            onClose={requestClose}
            onSubmit={props.onSubmit}
          />
        </Form>
      </Dialog>
      <Confirm
        isOpen={discardConfirmOpen}
        title="resources.certification-categories.text.discard_changes_title"
        content="resources.certification-categories.text.discard_changes_content"
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={confirmDiscard}
      />
    </>
  );

};
